import { combineReducers } from "redux";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import userReducer from "./slices/user";
import productReducer from "./slices/product";
import authJwtReducer from "./slices/authJwt";
import settingsReducer from "./slices/settings";
import calendarReducer from "./slices/calendar";
import taskReducer from "./slices/task";
import configurationReducer from "./slices/cartaRequest";
import notificationsReducer from "./slices/notifications";
import orderReducer from "./slices/order";
import mesasReducer from "./slices/mesas";
import planoElementsReducer from "./slices/planoElements";
import sessionsReducer from "./slices/session";
import printersReducer from "./slices/printer";
import categoriesReducer from "./slices/categories";
import cajaReducer from "./slices/caja";
import hubReducer from "./slices/hub";
import alergenReducer from "./slices/allergen";
import franquiciaReducer from "./slices/franquicias";
import analyticsReducer from "./slices/analytics";
import estadisticasV2Reducer from "./slices/estadisticasV2";
import promotionsReportReducer from "./slices/promotionsReport";
import logsReducer from "./slices/logs";
import CustomersReducer from "./slices/customer";
import EmployeeReducer from "./slices/employee";
import EmployeeRoleReducer from "./slices/employeeRoles";
import EmployeeSessionReducer from "./slices/employeeSession";
import EmployeeAuditReducer from "./slices/employeeAudit";
import EmployeeReportsReducer from "./slices/employeeReports";
import HolidayCalendarsReducer from "./slices/holidayCalendars";
import EmployeeContractsReducer from "./slices/employeeContracts";
import CompensationDaysReducer from "./slices/compensationDays";
import EmployeeAbsencesReducer from "./slices/employeeAbsences";
import loyaltyReducer from "./slices/loyalty";
import priceRulesReducer from "./slices/priceRules";
import RotationPatternsReducer from "./slices/rotationPatterns";
import ScheduleTemplatesReducer from "./slices/scheduleTemplates";
import InvoiceReducer from "./slices/invoices";
import historyPath from "./slices/historyPath";
import ticketsReducer from "./slices/tickets";
import discountsReducer from "./slices/discounts";
import comandasReducer from "./slices/comandas";
import errorsReducer from "./slices/errors";
import planReducer from "./slices/plan";
import auditReducer from "./slices/audit";
import devicesEnforcementReducer from "./slices/devices";
import aeatErrorsReducer from "./slices/aeatErrors";
import employeeCorrectionsReducer from "./slices/employeeCorrections";
import addOnsReducer from "./slices/addOns";
import activeReducer from "./slices/active";
import paymentMethods from "./slices/paymentMethods";
import distributorReducer from "./slices/distributors";
import displayReducer from "./slices/displays";
import schedulesV2Reducer from "./slices/schedulesV2";
import dinerQrReducer from "./slices/dinerQr";
import kitchenTicketsReducer from "./slices/kitchenTickets";
import arqueoExportsReducer from "./slices/arqueoExports";
import arqueoLiveReducer from "./slices/arqueoLive";
import scheduleAlertsReducer from "./slices/scheduleAlerts";
import rowsReducer from "./slices/rows";
import gestionReducer from "./slices/gestion";
import workerStatusReducer from "./slices/workerStatus";
import printerDiscoveryReducer from "./slices/printerDiscovery";
import printMetricsReducer from "./slices/printMetrics";
import networkStatusReducer from "./slices/networkStatus";
import onboardingReducer from "./slices/onboarding";
import bookingsReducer from "./slices/bookings";
import bookingSettingsReducer from "./slices/bookingSettings";
import waitlistReducer from "./slices/waitlist";
import cuentasReducer from "./slices/cuentas";
import observabilityReducer from "./slices/observability";
import liveDebugReducer from "./slices/liveDebug";
import integrationsReducer from "./slices/integrations";
import dailyReportsReducer from "./slices/dailyReports";
import reviewsReducer from "./slices/reviews";
import cashdrawerReducer from "./slices/cashdrawer";
import mesaLeasesReducer from "./slices/mesaLeases";

// ----------------------------------------------------------------------

// Blacklist agresivo: sólo persistimos lo que aporta al restaurante entre
// recargas (auth, prefs UI). Los slices de datos operativos (productos,
// mesas, pedidos, tickets, facturas, comandas) se rehidratan siempre del
// servidor — persistirlos:
//   1) Explota localStorage (varios MB por restaurante grande).
//   2) Deja RAM en Redux con datos obsoletos al arrancar.
//   3) Da la falsa sensación de "offline" que no es tal.
// `whitelist: []` mantiene el `persistReducer` en el store (no rompe el
// shape del state) pero le dice a redux-persist que NO guarde nada.

const rootPersistConfig = {
  key: "root",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: [],
};

// 2026-09-24 · Persistir el carrito. Whitelist estaba vacía → al
// recargar el user perdía todo lo que había añadido. Ahora se guardan
// `checkout` (el cart en curso + subtotales) y `productoNota` (notas
// por producto en el editor). El resto del slice se rehidrata con el
// catálogo del backend en cada arranque, así que no vale la pena
// persistirlo.
const productPersistConfig = {
  key: "product",
  storage: storage,
  keyPrefix: "redux-",
  // checkoutHistory = borrador por mesa de productos aún no guardados/comandados.
  whitelist: ["checkout", "productoNota", "checkoutHistory"],
};

const configurationPersistConfig = {
  key: "configuration",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: ["configuration"],
};

const authPersistConfig = {
  key: "authJwt",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: [
    "isAuthenticated",
    "claims",
    "isSessionAuthenticated",
    "session",
    "user",
  ],
};

const orderPersistConfig = {
  key: "order",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: [],
};

const notificationPersistConfig = {
  key: "notification",
  storage: storage,
  keyPrefix: "redux-",
  // Antes tenía "whitlist" (typo) → redux-persist lo ignoraba y persistía
  // TODO el slice, que puede crecer sin cota (adminNotifications acumula).
  whitelist: [],
};

const mesasPersistConfig = {
  key: "mesas",
  storage: storage,
  keyPrefix: "redux-",
  // Solo la última mesa seleccionada: al recargar se restaura la selección
  // (o se limpia el carrito si ya no se puede). La lista se pide siempre.
  whitelist: ["lastSelectedId"],
};

const printersPersistConfig = {
  key: "printers",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: [],
};

const sessionsPersistConfig = {
  key: "sessions",
  storage: storage,
  keyPrefix: "redux-",
  // lastPayment persistido (Fase 1 TPV): el "Último cobro" sobrevive a un F5.
  whitelist: ["sessions", "session", "currentHeight", "currentFilterName", "lastPayment"],
};

const categoriesPersistConfig = {
  key: "categories",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: [],
};

const cajaPersistConfig = {
  key: "caja",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: [],
};

const hubPersisConfig = {
  key: "hub",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: [],
};

const settingsPersistConfig = {
  key: "settings",
  storage: storage,
  keyPrefix: "redux-",
};

const alergenPersistConfig = {
  key: "alergenos",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: [],
};

const customersPersistConfig = {
  key: "customers",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: [],
};

const employeesPersistConfig = {
  key: "employees",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: [
    "employees",
    "employee",
    "currentActiveEmployee",
    "currentClocking",
    "openEmployeeModal",
    "currentTab",
    "clockings",
    "kioskOpen",
    "kioskManual",
  ],
};

const employeeRolePersistConfig = {
  key: "employeeRoles",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: ["employeeRoles", "role"],
};

const invoicePersistConfig = {
  key: "invoices",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: [],
};

const historyPathPersistConfig = {
  key: "historyPath",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: [],
};

const TicketsPersistConfig = {
  key: "tickets",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: [],
};

const ComandasPersistConfig = {
  key: "comandas",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: [],
};

const userPersistConfig = {
  key: "user",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: ["user", "users"],
};

const discountsPersistConfig = {
  key: "discounts",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: [],
};

const planPersistConfig = {
  key: "plan",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: [],
};

const errorsPersistConfig = {
  key: "errors",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: [],
};

const activePersistConfig = {
  key: "active",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: ["activeMesa", "activeSession"],
};

const paymentMethodsPersistConfig = {
  key: "paymentMethods",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: [],
};

// 2026-09-23 · Persistir el comensal identificado por el QR sobrevive
// recargas incluso si por accidente algún flujo hace localStorage.clear()
// nuclear (que además cliente-side no vuela redux-persist bajo la key
// `persist:*`). Backup redundante al `tmoQr:comensal:{id}` de localStorage.
const dinerQrPersistConfig = {
  key: "dinerQr",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: ["myComensal"],
};

const distributorPersistConfig = {
  key: "distributors",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: ["distributors", "distributor"],
};

const displaysPersistConfig = {
  key: "displays",
  storage: storage,
  keyPrefix: "redux-",
  whitelist: ["displays", "display"],
};

const rowsPersistConfig = {
  key: "rows",
  storage,
  keyPrefix: "redux-",
  whitelist: [],
};

const rootReducer = combineReducers({
  user: persistReducer(userPersistConfig, userReducer),
  task: taskReducer,
  settings: persistReducer(settingsPersistConfig, settingsReducer),
  calendar: calendarReducer,
  notification: persistReducer(notificationPersistConfig, notificationsReducer),
  product: persistReducer(productPersistConfig, productReducer),
  authJwt: persistReducer(authPersistConfig, authJwtReducer),
  configuration: persistReducer(
    configurationPersistConfig,
    configurationReducer,
  ),
  order: persistReducer(orderPersistConfig, orderReducer),
  mesas: persistReducer(mesasPersistConfig, mesasReducer),
  planoElements: planoElementsReducer,
  sessions: persistReducer(sessionsPersistConfig, sessionsReducer),
  printers: printersReducer,
  categories: persistReducer(categoriesPersistConfig, categoriesReducer),
  caja: cajaReducer,
  hub: persistReducer(hubPersisConfig, hubReducer),
  alergen: persistReducer(alergenPersistConfig, alergenReducer),
  franquicia: franquiciaReducer,
  analytics: analyticsReducer,
  estadisticasV2: estadisticasV2Reducer,
  promotionsReport: promotionsReportReducer,
  logs: logsReducer,
  customers: CustomersReducer,
  employees: persistReducer(employeesPersistConfig, EmployeeReducer),
  employeeRoles: persistReducer(employeeRolePersistConfig, EmployeeRoleReducer),
  employeeSession: EmployeeSessionReducer,
  employeeAudit: EmployeeAuditReducer,
  employeeReports: EmployeeReportsReducer,
  holidayCalendars: HolidayCalendarsReducer,
  employeeContracts: EmployeeContractsReducer,
  compensationDays: CompensationDaysReducer,
  employeeAbsences: EmployeeAbsencesReducer,
  loyalty: loyaltyReducer,
  priceRules: priceRulesReducer,
  rotationPatterns: RotationPatternsReducer,
  scheduleTemplates: ScheduleTemplatesReducer,
  invoices: InvoiceReducer,
  historyPath: persistReducer(historyPathPersistConfig, historyPath),
  tickets: ticketsReducer,
  discounts: discountsReducer,
  comandas: comandasReducer,
  errors: persistReducer(errorsPersistConfig, errorsReducer),
  plan: persistReducer(planPersistConfig, planReducer),
  audit: auditReducer,
  devicesEnforcement: devicesEnforcementReducer,
  aeatErrors: aeatErrorsReducer,
  employeeCorrections: employeeCorrectionsReducer,
  addOns: addOnsReducer,
  active: persistReducer(activePersistConfig, activeReducer),
  paymentMethods: persistReducer(paymentMethodsPersistConfig, paymentMethods),
  distributors: persistReducer(distributorPersistConfig, distributorReducer),
  displays: persistReducer(displaysPersistConfig, displayReducer),
  schedulesV2: schedulesV2Reducer,
  dinerQr: persistReducer(dinerQrPersistConfig, dinerQrReducer),
  kitchenTickets: kitchenTicketsReducer,
  arqueoExports: arqueoExportsReducer,
  arqueoLive: arqueoLiveReducer,
  scheduleAlerts: scheduleAlertsReducer,
  rows: persistReducer(rowsPersistConfig, rowsReducer),
  gestion: gestionReducer,
  workerStatus: workerStatusReducer,
  printerDiscovery: printerDiscoveryReducer,
  printMetrics: printMetricsReducer,
  networkStatus: networkStatusReducer,
  onboarding: onboardingReducer,
  bookings: bookingsReducer,
  bookingSettings: bookingSettingsReducer,
  waitlist: waitlistReducer,
  cuentas: cuentasReducer,
  observability: observabilityReducer,
  liveDebug: liveDebugReducer,
  integrations: integrationsReducer,
  dailyReports: dailyReportsReducer,
  reviews: reviewsReducer,
  cashdrawer: cashdrawerReducer,
  mesaLeases: mesaLeasesReducer,
});

export { rootPersistConfig, rootReducer };
